import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Check, CheckCircle, Camera, Upload, Trash2 } from "lucide-react";
import { useTranslation } from "@/hooks/useTranslation";
import { QuickCheckSettings } from "@/types/checklist";

interface UploadedPhoto {
  id: string;
  file: File;
  previewUrl: string;
}

interface QuickCheckTaskUIProps {
  taskIndex: number;
  taskText: string;
  roomName: string;
  settings: QuickCheckSettings;
  readOnly?: boolean;
  isVerified: boolean;
  photos: UploadedPhoto[];
  onVerify: () => void;
  onAddPhotos: (files: File[]) => void;
  onRemovePhoto: (id: string) => void;
}

const QuickCheckTaskUI = ({
  taskIndex,
  taskText,
  roomName,
  settings,
  readOnly = false,
  isVerified,
  photos,
  onVerify,
  onAddPhotos,
  onRemovePhoto,
}: QuickCheckTaskUIProps) => {
  const { tDynamic, isRtl } = useTranslation();
  
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      onAddPhotos(Array.from(files));
    }
    e.target.value = "";
  };

  const canVerify = !settings.require_photo || photos.length > 0;

  return (
    <div className="space-y-4 pb-4 border-b last:border-b-0" dir={isRtl ? "rtl" : "ltr"}>
      {/* Task header */}
      <div className="flex items-start gap-3 py-2">
        <span className="text-sm font-medium text-muted-foreground min-w-[24px] pt-0.5 shrink-0">
          {taskIndex + 1}.
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <Badge variant="secondary" className="gap-1 text-xs bg-blue-100 text-blue-700 border-blue-200">
              <CheckCircle className="w-3 h-3" />
              {tDynamic("Quick Check")}
            </Badge>
            {isVerified && (
              <Badge variant="secondary" className="gap-1 text-xs bg-green-100 text-green-700 border-green-200">
                <Check className="w-3 h-3" />
                {tDynamic("Verified")}
              </Badge>
            )}
          </div>
          <span className="text-base font-medium leading-snug block">{taskText}</span>
        </div>
      </div>

      {/* Photos section - only if require_photo is true */}
      {settings.require_photo && (
        <div className="space-y-3">
          <p className="text-sm font-medium">{tDynamic("Proof photos (required)")}</p>
          <p className="text-xs text-muted-foreground">
            {tDynamic("Upload photos to confirm this task is complete.")}
          </p>
          <Label className="text-sm text-muted-foreground flex items-center gap-2 sr-only">
            <Camera className="w-4 h-4" />
            {tDynamic("Photo (required)")}
          </Label>
          
          {/* Photo previews */}
          {photos.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {photos.map((photo) => (
                <div key={photo.id} className="relative group">
                  <img
                    src={photo.previewUrl}
                    alt="Quick check photo"
                    className="w-20 h-20 sm:w-16 sm:h-16 object-cover rounded-lg border"
                  />
                  {!readOnly && (
                    <button
                      type="button"
                      onClick={() => onRemovePhoto(photo.id)}
                      className="absolute -top-1 -right-1 w-5 h-5 sm:w-4 sm:h-4 bg-destructive text-destructive-foreground rounded-full flex items-center justify-center shadow touch-manipulation"
                    >
                      <Trash2 className="w-3 h-3 sm:w-2.5 sm:h-2.5" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
          
          {/* Upload button */}
          {!readOnly && (
            <label className="flex items-center justify-center gap-2 w-full h-14 sm:h-12 border-2 border-dashed border-border rounded-xl cursor-pointer hover:border-primary/50 hover:bg-muted/30 transition-colors active:bg-muted/50">
              <Upload className="w-5 h-5 sm:w-4 sm:h-4 text-muted-foreground" />
              <span className="text-base sm:text-sm text-muted-foreground font-medium">
                {tDynamic("Upload photo")}
              </span>
              <Input
                type="file"
                accept="image/*"
                multiple
                onChange={handleFileSelect}
                className="hidden"
                disabled={readOnly}
              />
            </label>
          )}
        </div>
      )}

      {/* Mark Verified button - sticky on mobile */}
      {!readOnly && (
        <div className="pt-2">
          <Button
            type="button"
            onClick={onVerify}
            disabled={!canVerify || isVerified}
            className={`w-full h-14 sm:h-12 text-base font-semibold rounded-full transition-all ${
              isVerified
                ? "bg-green-500 hover:bg-green-500 text-white"
                : canVerify
                  ? "bg-primary hover:bg-primary/90 text-primary-foreground"
                  : "bg-muted text-muted-foreground"
            }`}
          >
            {isVerified ? (
              <>
                <Check className="w-5 h-5 mr-2" />
                {tDynamic("Verified")}
              </>
            ) : (
              <>
                <CheckCircle className="w-5 h-5 mr-2" />
                {tDynamic("Mark Verified")}
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  );
};

export default QuickCheckTaskUI;
